/**
 * The purpose of this file is provide lookup of different display field types
 * by the id of a display field. It provides the following:
 * --- Required fields ---
 * label: The label to display for the display field
 * unit: The base unit of the display field (i.e. string, number, date, etc.)
 * formatUnit: The unit used to format a prefix and suffix around the value
 * --- 1 to 3 of the following keys should exist for every object ---
 * reportAllKey?: They key of the value when data is coming from a report all blob
 * salesforceKey?: They key of the value when data is coming from a salesforce blob
 * firestoreKey?: They key of the value when data is coming from a firestore blob
 */

export interface FieldTypeDefinition {
  label: string;
  /** Required by convention, but one legacy entry (lastModifiedBy) omits it. */
  unit?: string;
  formatUnit?: string;
  arcGisKey?: string;
  salesforceKey?: string;
  firestoreKey?: string;
  reportAllKey?: string;
  openStreetMapKey?: string;
  bqHeader?: string;
  pickerOptions?: string[];
  format?: string;
}

// fieldTypes
const fieldTypes: Record<string, FieldTypeDefinition> = {
  // #region --- GENERAL --- //
  name: {
    label: 'Name',
    unit: 'string',
    firestoreKey: 'Name',
    salesforceKey: 'Name',
    arcGisKey: 'name',
    openStreetMapKey: 'name',
  },
  // #endregion
  // #region --- BUILDINGS --- //
  entityName: {
    label: 'Entity Name',
    unit: 'string',
    salesforceKey: 'Entity Name',
    arcGisKey: 'entity_nam',
  },
  buildingArea: {
    label: 'Building Area',
    unit: 'number',
    formatUnit: 'squareFeet',
    salesforceKey: 'Building Area',
  },
  buildingWidth: {
    label: 'Building Width',
    unit: 'number',
    formatUnit: 'feet',
    salesforceKey: 'Building Width',
  },
  currentMlaRent: {
    label: 'Current MLA Rent',
    unit: 'number',
    formatUnit: 'pricePerSquareFoot',
    salesforceKey: 'Current MLA',
  },
  siteCamera: {
    label: 'Site Camera',
    unit: 'link',
    salesforceKey: 'Site Camera',
  },
  yearBuilt: {
    label: 'Year Built',
    unit: 'string',
    formatUnit: 'year',
    firestoreKey: 'Year Built',
    salesforceKey: 'Year Built',
  },
  brochure: {
    label: 'Brochure',
    unit: 'link',
    salesforceKey: 'Brochure',
  },
  taxBill: {
    label: 'Tax Bill',
    unit: 'number',
    formatUnit: 'taxBill',
    salesforceKey: 'Tax Bill',
  },
  address: {
    label: 'Address',
    unit: 'address',
    formatUnit: 'address',
  },
  buildingSize: {
    label: 'Building Size',
    unit: 'number',
    formatUnit: 'squareFeet',
    firestoreKey: 'Building Size',
  },
  clearHeight: {
    label: 'Clear Height',
    unit: 'number',
    formatUnit: 'feet',
    firestoreKey: 'Clear Height',
    salesforceKey: 'Clear Height',
  },
  county: {
    label: 'County',
    unit: 'string',
    firestoreKey: 'County',
    reportAllKey: 'county_name',
    arcGisKey: 'county',
  },
  developer: {
    label: 'Developer',
    unit: 'string',
    firestoreKey: 'Developer',
  },
  listingBroker: {
    label: 'Listing Broker',
    unit: 'string',
    firestoreKey: 'Listing Broker',
  },
  owner: {
    label: 'Owner',
    unit: 'string',
    firestoreKey: 'Owner',
    reportAllKey: 'owner',
  },
  buildingStage: {
    label: 'Building Stage',
    unit: 'picker',
    pickerOptions: ['', 'Pipeline', 'Construction', 'Complete', 'Fully Leased'],
    firestoreKey: 'Building Stage',
    salesforceKey: 'Building Stage',
  },
  buildingType: {
    label: 'Building Type',
    unit: 'multiPicker',
    pickerOptions: ['Shallow Bay', 'Crossdock', 'Single Load', 'Cold Storage'],
    firestoreKey: 'Building Type',
  },
  constructionCompletionDate: {
    label: 'Construction Completion Date',
    unit: 'date',
    firestoreKey: 'Construction Completion Date',
  },
  totalAvailable: {
    label: 'Total Available',
    unit: 'number',
    formatUnit: 'squareFeet',
    firestoreKey: 'Total Available',
    salesforceKey: 'Total Available',
  },
  leasedPercentage: {
    label: 'Leased Percentage',
    unit: 'number',
    formatUnit: 'percentage',
    firestoreKey: 'Leased Percentage',
  },
  lastModifiedBy: {
    label: 'Last Modified By',
  },
  schedule: {
    label: 'Schedule',
    unit: 'schedule',
  },
  massGrading: {
    label: 'Mass Grading',
    unit: 'date',
    salesforceKey: 'Mass Grading',
  },
  foundations: {
    label: 'Foundations',
    unit: 'date',
    salesforceKey: 'Foundations',
  },
  steelErection: {
    label: 'Steel Erection',
    unit: 'date',
    salesforceKey: 'Steel Erection',
  },
  roofStart: {
    label: 'Roof Start',
    unit: 'date',
    salesforceKey: 'Roof Start',
  },
  expectedTco: {
    label: 'Expected TCO',
    unit: 'date',
    salesforceKey: 'Expected TCO',
  },
  finalCompletion: {
    label: 'Final Completion',
    unit: 'date',
    salesforceKey: 'Final Completion',
  },
  propertyTypeOwner: {
    label: 'Property Type Owner',
    unit: 'string',
    firestoreKey: 'Property Type Owner',
    salesforceKey: 'Property Type Owner',
  },
  dataCenterOperator: {
    label: 'Data Center Operator',
    unit: 'string',
    firestoreKey: 'Data Center Operator',
    salesforceKey: 'Data Center Operator',
  },
  plantId: {
    label: 'Plant ID',
    unit: 'number',
  },
  plantName: {
    label: 'Plant Name',
    unit: 'string',
  },
  parentName: {
    label: 'Parent Name',
    unit: 'string',
  },
  hyperScalerFlag: {
    label: 'Hyperscaler Flag',
    unit: 'string',
    formatUnit: 'boolean',
  },
  ownerName: {
    label: 'Owner Name',
    unit: 'string',
  },
  capacity: {
    label: 'Capacity',
    unit: 'number',
    formatUnit: 'megaWatts',
  },
  squareFootage: {
    label: 'Square Footage',
    unit: 'number',
    formatUnit: 'squareFeet',
  },
  dcStatus: {
    label: 'Status',
    unit: 'string',
  },
  isoRTORegion: {
    label: 'ISO RTO Region',
    unit: 'string',
  },
  plantStartUpDate: {
    label: 'Plant Start Up Date',
    unit: 'date',
  },
  projectCount: {
    label: 'Project Count',
    unit: 'number',
  },
  constructionDurationWithCounts: {
    label: 'Construction Duration (months) with counts',
    unit: 'string',
  },
  tivUSDWithCounts: {
    label: 'TIV USD with counts',
    unit: 'string',
  },
  projectTypeWithCounts: {
    label: 'Project Type with counts',
    unit: 'string',
  },
  projectStageWithCounts: {
    label: 'Project Stage with counts',
    unit: 'string',
  },
  kickoffDateWithCounts: {
    label: 'Kickoff Date with counts',
    unit: 'string',
  },
  completionDateWithCounts: {
    label: 'Completion Date with counts',
    unit: 'string',
  },
  walt: {
    label: 'WALT',
    unit: 'number',
    formatUnit: 'years',
  },
  goingInCapRate: {
    label: 'Going-In Cap Rate',
    unit: 'number',
    formatUnit: 'percentage',
  },
  totalBuildingArea: {
    label: 'Total Building Area',
    unit: 'number',
    formatUnit: 'squareFeet',
  },
  weightedAvgMarketRent: {
    label: 'Weighted Avg Market Rent',
    unit: 'number',
    formatUnit: 'pricePerSquareFoot',
  },
  weightedAvgOmRent: {
    label: 'Weighted Avg OM Rent',
    unit: 'number',
    formatUnit: 'pricePerSquareFoot',
  },
  mtmOpportunity: {
    label: 'MTM Opportunity',
    unit: 'number',
    formatUnit: 'percentage',
  },
  // #endregion
  // #region --- PROJECTS --- //
  unitCount: {
    label: 'Unit Count',
    unit: 'number',
    salesforceKey: 'unitCount',
    firestoreKey: 'Unit Count',
  },
  utilities: {
    label: 'Utilities',
    unit: 'utilities',
  },
  // #endregion
  // #region --- COMPS --- //
  premiseArea: {
    label: 'Premise Area',
    unit: 'squareFeet',
    firestoreKey: 'Premise Area',
    salesforceKey: 'Premise Area',
  },
  leaseSigned: {
    label: 'Lease Signed',
    unit: 'number',
    formatUnit: 'year',
    firestoreKey: 'Lease Signed',
    salesforceKey: 'Lease Signed',
  },
  commencementDate: {
    label: 'Commencement Date',
    unit: 'date',
    firestoreKey: 'Commencement Date',
    salesforceKey: 'Commencement Date',
  },
  leaseExpiration: {
    label: 'Lease Expiration',
    unit: 'date',
    firestoreKey: 'Lease Expiration',
    salesforceKey: 'Lease Expiration',
  },
  leaseType: {
    label: 'Lease Type',
    unit: 'string',
    firestoreKey: 'Lease Type',
    salesforceKey: 'Lease Type',
  },
  leaseTerm: {
    label: 'Lease Term',
    unit: 'number',
    formatUnit: 'months',
    firestoreKey: 'Lease Term',
    salesforceKey: 'Lease Term',
  },
  term: {
    label: 'Term',
    unit: 'number',
    formatUnit: 'months',
    firestoreKey: 'Term',
    salesforceKey: 'Term',
  },
  baseRent: {
    label: 'Base Rent',
    unit: 'number',
    formatUnit: 'pricePerSquareFoot',
    firestoreKey: 'Base Rent',
    salesforceKey: 'Base Rent',
  },
  effectiveRent: {
    label: 'Effective Rent',
    unit: 'number',
    formatUnit: 'pricePerSquareFoot',
    firestoreKey: 'Effective Rent',
    salesforceKey: 'Effective Rent',
  },
  annualEscalations: {
    label: 'Annual Escalations',
    unit: 'number',
    formatUnit: 'percentage',
    firestoreKey: 'Annual Escalations',
    salesforceKey: 'Escalations',
  },
  tiPerSf: {
    label: 'TI Per SF',
    unit: 'number',
    formatUnit: 'pricePerSquareFoot',
    firestoreKey: 'TI Per SF',
    salesforceKey: 'TI Per SF',
  },
  freeRent: {
    label: 'Free Rent',
    unit: 'number',
    formatUnit: 'months',
    firestoreKey: 'Free Rent',
    salesforceKey: 'Free Rent',
  },
  rentAbatements: {
    label: 'Rent Abatements',
    unit: 'number',
    formatUnit: 'months',
    firestoreKey: 'Rent Abatements',
    salesforceKey: 'Rent Abatements',
  },
  rentEscalator: {
    label: 'Rent Escalator',
    unit: 'number',
    formatUnit: 'percentage',
    firestoreKey: 'Rent Escalator',
    salesforceKey: 'Rent Escalator',
  },
  startingRent: {
    label: 'Starting Rent',
    unit: 'number',
    formatUnit: 'pricePerSquareFoot',
    firestoreKey: 'Starting Rent',
    salesforceKey: 'Starting Rent',
  },
  vacateDate: {
    label: 'Vacate Date',
    unit: 'date',
    firestoreKey: 'Vacate Date',
    salesforceKey: 'Vacate Date',
  },
  officeFinish: {
    label: 'Office Finish',
    unit: 'number',
    formatUnit: 'percentage',
    firestoreKey: 'Office Finish',
    salesforceKey: 'Office Finish',
  },
  landlord: {
    label: 'Landlord',
    unit: 'string',
    firestoreKey: 'Landlord',
    salesforceKey: 'Landlord',
  },
  startDate: {
    label: 'Start Date',
    unit: 'date',
    firestoreKey: 'Start Date',
    salesforceKey: 'Start Date',
    arcGisKey: 'startdate',
    openStreetMapKey: 'start_date',
  },
  signDate: {
    label: 'Sign Date',
    unit: 'date',
    firestoreKey: 'Sign Date',
    salesforceKey: 'Sign Date',
  },
  source: {
    label: 'Source',
    unit: 'string',
    firestoreKey: 'Source',
    salesforceKey: 'Source',
    openStreetMapKey: 'source',
  },
  expirationDate: {
    label: 'Expiration Date',
    unit: 'date',
  },
  currentRent: {
    label: 'Current Rent',
    unit: 'number',
    formatUnit: 'pricePerSquareFoot',
  },
  buyer: {
    label: 'Buyer',
    unit: 'string',
    firestoreKey: 'Buyer',
    salesforceKey: 'Buyer',
  },
  occupancy: {
    label: 'Occupancy',
    unit: 'number',
    formatUnit: 'percentage',
    firestoreKey: 'occupancy',
  },
  buildingCount: {
    label: '# of Buildings',
    unit: 'number',
    formatUnit: 'buildingCount',
    firestoreKey: 'buildingCount',
  },
  seller: {
    label: 'Seller',
    unit: 'string',
    firestoreKey: 'Seller',
    salesforceKey: 'Seller',
  },
  buildingSf: {
    label: 'Building SF',
    unit: 'number',
    formatUnit: 'squareFeet',
    firestoreKey: 'Building SF',
    salesforceKey: 'Building SF',
    reportAllKey: 'bldg_sqft',
  },
  acreage: {
    label: 'Acreage',
    unit: 'number',
    formatUnit: 'acreage',
    firestoreKey: 'Acreage',
    salesforceKey: 'Acreage',
  },
  salePrice: {
    label: 'Sale Price',
    unit: 'number',
    formatUnit: 'currency',
    firestoreKey: 'Sale Price',
    salesforceKey: 'Sale Price',
    reportAllKey: 'sale_price',
  },
  saleDate: {
    label: 'Sale Date',
    unit: 'date',
    firestoreKey: 'Sale Date',
    salesforceKey: 'Sale Date',
    reportAllKey: 'trans_date',
  },
  pricePerAcre: {
    label: 'Price per Acre',
    unit: 'number',
    formatUnit: 'pricePerAcre',
    firestoreKey: 'Price per Acre',
    salesforceKey: 'Price per Acre',
  },
  pricePerSf: {
    label: 'Price per sf',
    unit: 'number',
    formatUnit: 'pricePerSquareFoot',
    firestoreKey: 'Price per sf',
    salesforceKey: 'Price per sf',
  },
  pricePerFar: {
    label: 'Price per FAR',
    unit: 'number',
    formatUnit: 'pricePerFar',
    firestoreKey: 'Price per FAR',
    salesforceKey: 'Price per FAR',
  },
  areaSold: {
    label: 'Area Sold',
    unit: 'number',
    formatUnit: 'squareFeet',
    firestoreKey: 'Area Sold',
    salesforceKey: 'Area Sold',
  },
  capRate: {
    label: 'Cap Rate',
    unit: 'number',
    formatUnit: 'percentage',
    firestoreKey: 'Cap Rate',
    salesforceKey: 'Cap Rate',
  },
  latestPremiseArea: {
    label: 'Latest Premise Area',
    unit: 'number',
    formatUnit: 'squareFeet',
    firestoreKey: 'Latest Premise Area Option',
    salesforceKey: 'Latest Premise Area Option',
  },
  latestBaseRent: {
    label: 'Latest Base Rent',
    unit: 'number',
    formatUnit: 'pricePerSquareFoot',
    firestoreKey: 'Latest Base Rent Option',
    salesforceKey: 'Base_Rent_psf_Latest_Option__c',
  },
  rfpStage: {
    label: 'Lease Deal Stage',
    unit: 'string',
    firestoreKey: 'Lease Deal Stage',
    salesforceKey: 'Lease Deal Stage',
  },
  submitted: {
    label: 'Submitted',
    unit: 'date',
    firestoreKey: 'Submitted',
    salesforceKey: 'Submitted',
  },
  requirementArea: {
    label: 'Requirement Area',
    unit: 'number',
    formatUnit: 'squareFeet',
    firestoreKey: 'Requirement Area',
    salesforceKey: 'Requirement Area',
  },
  averageSize: {
    label: 'Average Size',
    unit: 'number',
    formatUnit: 'squareFeet',
    firestoreKey: 'Average Size',
    salesforceKey: 'Average Size',
  },
  marketRentPSF: {
    label: 'Market Rent PSF',
    unit: 'number',
    formatUnit: 'pricePerSquareFoot',
    firestoreKey: 'Market Rent PSF',
    salesforceKey: 'Market Rent PSF',
  },
  percentOccupied: {
    label: 'Percent Occupied',
    unit: 'number',
    formatUnit: 'percentage',
    firestoreKey: 'Percent Occupied',
  },
  cashEquity: {
    label: 'Cash Equity',
    unit: 'string',
    formatUnit: 'currency',
    firestoreKey: 'Cash Equity',
    salesforceKey: 'Cash Equity',
  },
  closingDate: {
    label: 'Closing Date',
    unit: 'string',
    formatUnit: 'date',
    firestoreKey: 'Closing Date',
    salesforceKey: 'Closing Date',
  },
  debtAmortization: {
    label: 'Debt Amortization',
    unit: 'string',
    formatUnit: 'years',
    firestoreKey: 'Debt Amortization',
    salesforceKey: 'Debt Amortization',
  },
  debtStatus: {
    label: 'Debt Status',
    unit: 'string',
    formatUnit: 'string',
    firestoreKey: 'Debt Status',
    salesforceKey: 'Debt Status',
  },
  debtTerms: {
    label: 'Debt Terms',
    unit: 'string',
    formatUnit: 'string',
    firestoreKey: 'Debt Terms',
    salesforceKey: 'Debt Terms',
  },
  interestRate: {
    label: 'Interest Rate',
    unit: 'string',
    formatUnit: 'string',
    firestoreKey: 'Interest Rate',
    salesforceKey: 'Interest Rate',
  },
  investmentStage: {
    label: 'Investment Stage',
    unit: 'string',
    formatUnit: 'string',
    firestoreKey: 'Investment Stage',
    salesforceKey: 'Investment Stage',
  },
  lender: {
    label: 'Lender',
    unit: 'string',
    formatUnit: 'string',
    firestoreKey: 'Lender',
    salesforceKey: 'Lender',
  },
  loanAmount: {
    label: 'Loan Amount',
    unit: 'string',
    formatUnit: 'currency',
    firestoreKey: 'Loan Amount',
    salesforceKey: 'Loan Amount',
  },
  loanType: {
    label: 'Loan Type',
    unit: 'string',
    formatUnit: 'string',
    firestoreKey: 'Loan Type',
    salesforceKey: 'Loan Type',
  },
  maturityDate: {
    label: 'Maturity Date',
    unit: 'string',
    formatUnit: 'date',
    firestoreKey: 'Maturity Date',
    salesforceKey: 'Maturity Date',
  },
  primaryInvestor: {
    label: 'Primary Investor',
    unit: 'string',
    formatUnit: 'string',
    firestoreKey: 'Primary Investor',
    salesforceKey: 'Primary Investor',
  },
  rateType: {
    label: 'Rate Type',
    unit: 'string',
    formatUnit: 'string',
    firestoreKey: 'Rate Type',
    salesforceKey: 'Rate Type',
  },
  reference: {
    label: 'Reference',
    unit: 'string',
    formatUnit: 'string',
    firestoreKey: 'Reference',
    salesforceKey: 'Reference',
  },
  totalCapitalAuthorized: {
    label: 'Total Capital Authorized',
    unit: 'string',
    formatUnit: 'currency',
    firestoreKey: 'Total Capital Authorized',
    salesforceKey: 'Total Capital Authorized',
  },
  transactionValue: {
    label: 'Transaction Value',
    unit: 'string',
    formatUnit: 'currency',
    firestoreKey: 'Transaction Value',
    salesforceKey: 'Transaction Value',
  },
  // #endregion
  // #region --- PARCEL --- //
  parcelId: {
    label: 'Parcel ID',
    unit: 'string',
    reportAllKey: 'parcel_id',
  },
  lastUpdated: {
    label: 'Last Updated',
    unit: 'string',
    reportAllKey: 'last_updated',
  },
  mailingAddress: {
    label: 'Mailing Address',
    unit: 'address',
  },
  acreageWithSameOwner: {
    label: 'Acreage with Same Owner',
    unit: 'string',
    reportAllKey: 'acreage_adjacent_with_sameowner',
  },
  ownerOccupied: {
    label: 'Owner Occupied',
    unit: 'string',
    reportAllKey: 'owner_occupied',
  },
  marketLandValue: {
    label: 'Market Land Value',
    unit: 'string',
    reportAllKey: 'mkt_val_land',
  },
  marketBuildingValue: {
    label: 'Market Building Value',
    unit: 'string',
    reportAllKey: 'mkt_val_bldg',
  },
  marketTotalValue: {
    label: 'Market Total Value',
    unit: 'string',
    reportAllKey: 'mkt_val_tot',
  },
  numberFloors: {
    label: 'Number of Floors',
    unit: 'string',
    reportAllKey: 'story_height',
  },
  numberBuildings: {
    label: 'Number of Buildings',
    unit: 'string',
    reportAllKey: 'buildings',
  },
  neighborhoodCode: {
    label: 'Neighborhood Code',
    unit: 'string',
    reportAllKey: 'ngh_code',
  },
  landUseCode: {
    label: 'Land Use Code',
    unit: 'string',
    reportAllKey: 'land_use_code',
  },
  landUseClass: {
    label: 'Land Use Class',
    unit: 'string',
    reportAllKey: 'land_use_class',
  },
  uspsClassification: {
    label: 'USPS Classification',
    unit: 'string',
    reportAllKey: 'usps_residential',
  },
  legalDescription: {
    label: 'Legal Description',
    unit: 'string',
  },
  elevation: {
    label: 'Elevation',
    unit: 'string',
    reportAllKey: 'elevation',
  },
  acreageDeeded: {
    label: 'Acreage Deeded',
    unit: 'string',
    reportAllKey: 'acreage_deeded',
  },
  acreageCalculated: {
    label: 'Acreage Calculated',
    unit: 'acreage',
    reportAllKey: 'acreage_calc',
  },
  websiteLink: {
    label: 'Link to Assessor\'s Website',
    unit: 'link',
  },
  robustId: {
    label: 'ReportAll Robust Id',
    unit: 'string',
    reportAllKey: 'robust_id',
  },
  townshipRange: {
    label: 'PLSS Section, Township, Range',
    unit: 'string',
    reportAllKey: 'section_township_range',
  },
  // #endregion
  // #region -- ITAMLINK TAX PARCEL -- //
  propertyName: {
    label: 'Property Name',
    unit: 'string',
  },
  parcelNumber: {
    label: 'Parcel Number',
    unit: 'string',
  },
  parcelNumber2: {
    label: 'Parcel Number 2',
    unit: 'string',
  },
  propertyAddress: {
    label: 'Property Address',
    unit: 'address',
    formatUnit: 'address',
  },
  propertyCounty: {
    label: 'County/Jurisdiction',
    unit: 'string',
  },
  parcelAddress: {
    label: 'Parcel Address',
    unit: 'string',
  },
  parcelCounty: {
    label: 'Parcel County',
    unit: 'string',
  },
  parcelGrossBuildingArea: {
    label: 'Gross Building Area',
    unit: 'number',
    formatUnit: 'squareFeet',
  },
  parcelLandArea: {
    label: 'Land Area',
    unit: 'number',
    formatUnit: 'acreage',
  },
  // #endregion
  // #region -- TRANSMISSION LINES -- //
  naicsDesc: {
    label: 'Description',
    unit: 'string',
    arcGisKey: 'naics_desc',
  },
  transmissionLineOwner: {
    label: 'OWNER',
    unit: 'string',
    arcGisKey: 'owner',
  },
  sourceDate: {
    label: 'Source Date',
    unit: 'date',
    arcGisKey: 'sourcedate',
  },
  status: {
    label: 'STATUS',
    unit: 'string',
    arcGisKey: 'status',
  },
  sub1: {
    label: 'Substation Start',
    unit: 'string',
    arcGisKey: 'sub_1',
  },
  sub2: {
    label: 'Substation End',
    unit: 'string',
    arcGisKey: 'sub_2',
  },
  type: {
    label: 'TYPE',
    unit: 'string',
    arcGisKey: 'type',
    openStreetMapKey: 'type',
  },
  voltage: {
    label: 'VOLTAGE',
    unit: 'kiloVolts',
    arcGisKey: 'voltage',
    openStreetMapKey: 'voltage',
  },
  // #endregion
  // #region --- Service Territories --- //
  territoryName: {
    label: 'Name',
    unit: 'string',
    arcGisKey: 'name',
  },
  territoryAddress: {
    label: 'Address',
    unit: 'string',
    arcGisKey: 'address',
  },
  territoryCity: {
    label: 'City',
    unit: 'string',
    arcGisKey: 'city',
  },
  territoryState: {
    label: 'State',
    unit: 'string',
    arcGisKey: 'state',
  },
  territoryZip: {
    label: 'Zip',
    unit: 'string',
    arcGisKey: 'zip',
  },
  territoryTelephone: {
    label: 'Telephone',
    unit: 'string',
    arcGisKey: 'telephone',
  },
  territoryType: {
    label: 'Type',
    unit: 'string',
    arcGisKey: 'type',
  },
  website: {
    label: 'Website',
    unit: 'string',
    arcGisKey: 'website',
  },
  regulated: {
    label: 'Regulated',
    unit: 'string',
    arcGisKey: 'regulated',
  },
  centralArea: {
    label: 'Central Area',
    unit: 'string',
    arcGisKey: 'cntrl_area',
  },
  planArea: {
    label: 'Plan Area',
    unit: 'string',
    arcGisKey: 'plan_area',
  },
  holdingCompany: {
    label: 'Holding Co',
    unit: 'string',
    arcGisKey: 'holding_co',
  },
  summerPeak: {
    label: 'Summer Peak',
    unit: 'string',
    arcGisKey: 'summr_peak',
  },
  winterPeak: {
    label: 'Winter Peak',
    unit: 'string',
    arcGisKey: 'wintr_peak',
  },
  summerCap: {
    label: 'Summer Cap',
    unit: 'string',
    arcGisKey: 'summer_cap',
  },
  winterCap: {
    label: 'Winter Cap',
    unit: 'string',
    arcGisKey: 'winter_cap',
  },
  netGen: {
    label: 'Net Gen',
    unit: 'string',
    arcGisKey: 'net_gen',
  },
  purchased: {
    label: 'Purchased',
    unit: 'string',
    arcGisKey: 'purchased',
  },
  netEx: {
    label: 'Net Ex',
    unit: 'string',
    arcGisKey: 'net_ex',
  },
  retailMWH: {
    label: 'Retail MWH',
    unit: 'string',
    arcGisKey: 'retail_mwh',
  },
  wSaleMWH: {
    label: 'Wholesale MWH',
    unit: 'string',
    arcGisKey: 'w_sale_mwh',
  },
  totalMWH: {
    label: 'Total MWH',
    unit: 'string',
    arcGisKey: 'total_mwh',
  },
  transMWH: {
    label: 'Trans MWH',
    unit: 'string',
    arcGisKey: 'trans_mwh',
  },
  customers: {
    label: 'Customers',
    unit: 'string',
    arcGisKey: 'customers',
  },
  // #endregion
  // #region --- SoCal Edison Network --- //
  substationId: {
    label: 'Substation ID',
    unit: 'string',
    arcGisKey: 'subst_id',
  },
  substationName: {
    label: 'Substation Name',
    unit: 'string',
    arcGisKey: 'sub_name',
  },
  systemName: {
    label: 'System Name',
    unit: 'string',
    arcGisKey: 'sys_name',
  },
  substationType: {
    label: 'Substation Type',
    unit: 'string',
    arcGisKey: 'sub_type',
  },
  substationVoltage: {
    label: 'Substation Voltage',
    unit: 'string',
    arcGisKey: 'substation_voltage',
  },
  existingGen: {
    label: 'Existing Gen',
    unit: 'string',
    arcGisKey: 'existing_gen',
  },
  queuedGen: {
    label: 'Queued Gen',
    unit: 'string',
    arcGisKey: 'queued_gen',
  },
  totalGen: {
    label: 'Total Gen',
    unit: 'string',
    arcGisKey: 'total_gen',
  },
  projectedLoad: {
    label: 'Projected Load',
    unit: 'string',
    arcGisKey: 'projected_load',
  },
  penetrationLevel: {
    label: 'Penetration Level',
    unit: 'string',
    arcGisKey: 'penetration_level',
  },
  maxRemainCap: {
    label: 'Max Remain Cap',
    unit: 'string',
    arcGisKey: 'max_remain_cap',
  },
  note: {
    label: 'Note',
    unit: 'string',
    arcGisKey: 'note',
  },
  circuitId: {
    label: 'Circuit ID',
    unit: 'string',
    arcGisKey: 'circuit_id',
  },
  circuitNo: {
    label: 'Circuit No',
    unit: 'string',
    arcGisKey: 'circuit_no',
  },
  circuitTypeDesc: {
    label: 'Circuit Type Desc',
    unit: 'string',
    arcGisKey: 'circuit_type_desc',
  },
  circuitVoltage: {
    label: 'Circuit Voltage',
    unit: 'string',
    arcGisKey: 'circuit_voltage',
  },
  circuitType: {
    label: 'Circuit Type',
    unit: 'string',
    arcGisKey: 'circuit_type',
  },
  circuitName: {
    label: 'Circuit Name',
    unit: 'string',
    arcGisKey: 'circt_nam',
  },
  // #endregion
  // #region --- Power Generators --- //
  nameplateCapacity: {
    label: 'Nameplate Capacity',
    unit: 'number',
    formatUnit: 'megaWatts',
    arcGisKey: 'sum_namepl',
  },
  plantStat: {
    label: 'State',
    unit: 'string',
    arcGisKey: 'plant_stat',
  },
  techType: {
    label: 'Technology',
    unit: 'string',
    arcGisKey: 'technology',
  },
  sector: {
    label: 'Sector',
    unit: 'string',
    arcGisKey: 'sector',
  },
  generatorIds: {
    label: 'Generator IDs',
    unit: 'string',
    arcGisKey: 'generator_',
  },
  // #endregion
  // #region Railroads
  railOwner: {
    label: 'Owner',
    unit: 'string',
    arcGisKey: 'rrowner1',
  },
  jointOwners: {
    label: 'Joint Owner(s)',
    unit: 'string',
    arcGisKey: 'owners_joint',
  },
  trackRights: {
    label: 'Track Rights',
    unit: 'string',
    arcGisKey: 'rights_joint',
  },
  railName: {
    label: 'Name',
    unit: 'string',
    arcGisKey: 'name',
  },
  // #endregion
  // #region --- OpenStreetMap --- //
  area: {
    label: 'Area',
    unit: 'number',
    formatUnit: 'squareMeters',
    openStreetMapKey: 'area',
  },
  circuits: {
    label: 'Circuits',
    unit: 'number',
    openStreetMapKey: 'circuits',
  },
  location: {
    label: 'Location',
    unit: 'string',
    openStreetMapKey: 'location',
    arcGisKey: 'location',
  },
  operator: {
    label: 'Operator',
    unit: 'string',
    openStreetMapKey: 'operator',
  },
  ref: {
    label: 'Ref',
    unit: 'string',
    openStreetMapKey: 'ref',
  },
  substance: {
    label: 'Substance',
    unit: 'string',
    openStreetMapKey: 'substance',
  },
  usage: {
    label: 'Usage',
    unit: 'string',
    openStreetMapKey: 'usage',
  },
  construction: {
    label: 'Construction',
    unit: 'boolean',
    openStreetMapKey: 'construction',
  },
  substation: {
    label: 'Substation',
    unit: 'boolean',
    openStreetMapKey: 'substation',
  },
  method: {
    label: 'Method',
    unit: 'string',
    openStreetMapKey: 'method',
  },
  output: {
    label: 'Output',
    unit: 'megaWatts',
    openStreetMapKey: 'output',
  },
  voltageTwo: {
    label: 'Voltage 2',
    unit: 'string',
    openStreetMapKey: 'voltage_2',
    format: 'kiloVolts',
  },
  tunnel: {
    label: 'Tunnel',
    unit: 'boolean',
    openStreetMapKey: 'tunnel',
  },
  line: {
    label: 'Line',
    unit: 'string',
    openStreetMapKey: 'line',
  },
  cables: {
    label: 'Cables',
    unit: 'string',
    openStreetMapKey: 'cables',
  },
  design: {
    label: 'Design',
    unit: 'string',
    openStreetMapKey: 'design',
  },
  diameter: {
    label: 'Diameter',
    unit: 'string',
    openStreetMapKey: 'diameter',
    format: 'inches',
  },
  electrical_output: {
    label: 'Electrical Output',
    unit: 'string',
    openStreetMapKey: 'electrical_output',
  },
  // #endregion
  // #region --- Nonattainment Areas & Designations --- //
  area_name: {
    label: 'Area Name',
    unit: 'string',
    arcGisKey: 'area_name',
  },
  daa_proposed_citation: {
    label: 'DAA Proposed Citation',
    unit: 'string',
    arcGisKey: 'daa_proposed_citation',
  },
  cdd_proposed_citation: {
    label: 'CDD Proposed Citation',
    unit: 'string',
    arcGisKey: 'cdd_proposed_citation',
  },
  cdd_proposed_pub_date: {
    label: 'CDD Proposed Pub Date',
    unit: 'date',
    arcGisKey: 'cdd_proposed_pub_date',
  },
  cdd_final_citation: {
    label: 'CDD Final Citation',
    unit: 'string',
    arcGisKey: 'cdd_final_citation',
  },
  daa_effective_date: {
    label: 'DAA Effective Date',
    unit: 'date',
    arcGisKey: 'daa_effective_date',
  },
  original_dv24: {
    label: 'Original DV24',
    unit: 'string',
    arcGisKey: 'original_dv24',
  },
  designation_effective_date: {
    label: 'Designation Effective Date',
    unit: 'date',
    arcGisKey: 'designation_effective_date',
  },
  daa_final_pub_date: {
    label: 'DAA Final Pub Date',
    unit: 'date',
    arcGisKey: 'daa_final_pub_date',
  },
  populationtotals_totpop_cy: {
    label: 'Population Totals',
    unit: 'number',
    arcGisKey: 'populationtotals_totpop_cy',
  },
  redesignation_effective_date: {
    label: 'Redesignation Effective Date',
    unit: 'date',
    arcGisKey: 'redesignation_effective_date',
  },
  epa_region_office: {
    label: 'EPA Region Office',
    unit: 'string',
    arcGisKey: 'epa_region_office',
  },
  state_name: {
    label: 'State Name',
    unit: 'string',
    arcGisKey: 'state_name',
  },
  designation_pub_date: {
    label: 'Designation Pub Date',
    unit: 'date',
    arcGisKey: 'designation_pub_date',
  },
  populationtotalspl94_totpop20: {
    label: 'Population Totals PL94',
    unit: 'number',
    arcGisKey: 'populationtotalspl94_totpop20',
  },
  redesignation_final_pub_date: {
    label: 'Redesignation Final Pub Date',
    unit: 'date',
    arcGisKey: 'redesignation_final_pub_date',
  },
  meets_naaqs: {
    label: 'Meets NAAQS',
    unit: 'string',
    arcGisKey: 'meets_naaqs',
  },
  redesignation_proposed_url: {
    label: 'Redesignation Proposed URL',
    unit: 'link',
    arcGisKey: 'redesignation_proposed_url',
  },
  classification_pub_date: {
    label: 'Classification Pub Date',
    unit: 'date',
    arcGisKey: 'classification_pub_date',
  },
  redesignation_proposed_pub_date: {
    label: 'Redesignation Proposed Pub Date',
    unit: 'date',
    arcGisKey: 'redesignation_proposed_pub_date',
  },
  composidplus: {
    label: 'Compos ID Plus',
    unit: 'string',
    arcGisKey: 'composidplus',
  },
  classification: {
    label: 'Classification',
    unit: 'string',
    arcGisKey: 'classification',
  },
  designation_url: {
    label: 'Designation URL',
    unit: 'link',
    arcGisKey: 'designation_url',
  },
  daa_final_citation: {
    label: 'DAA Final Citation',
    unit: 'string',
    arcGisKey: 'daa_final_citation',
  },
  designation_citation: {
    label: 'Designation Citation',
    unit: 'string',
    arcGisKey: 'designation_citation',
  },
  composite_id: {
    label: 'Composite ID',
    unit: 'string',
    arcGisKey: 'composite_id',
  },
  daa_final_url: {
    label: 'DAA Final URL',
    unit: 'link',
    arcGisKey: 'daa_final_url',
  },
  epa_region_roman: {
    label: 'EPA Region in Roman Numerals',
    unit: 'string',
    arcGisKey: 'epa_region_roman',
  },
  current_status: {
    label: 'Current Status',
    unit: 'string',
    arcGisKey: 'current_status',
  },
  population_2010: {
    label: 'Population 2010',
    unit: 'number',
    arcGisKey: 'population_2010',
  },
  redesignation_proposed_citatio: {
    label: 'Redesignation Proposed Citation',
    unit: 'string',
    arcGisKey: 'redesignation_proposed_citatio',
  },
  classification_effective_date: {
    label: 'Classification Effective Date',
    unit: 'date',
    arcGisKey: 'classification_effective_date',
  },
  classification_citation: {
    label: 'Classification Citation',
    unit: 'string',
    arcGisKey: 'classification_citation',
  },
  daa_proposed_url: {
    label: 'DAA Proposed URL',
    unit: 'link',
    arcGisKey: 'daa_proposed_url',
  },
  composid: {
    label: 'Compos ID',
    unit: 'string',
    arcGisKey: 'composid',
  },
  epa_region: {
    label: 'EPA Region',
    unit: 'string',
    arcGisKey: 'epa_region',
  },
  state_abbreviation: {
    label: 'State Abbreviation',
    unit: 'string',
    arcGisKey: 'state_abbreviation',
  },
  design_value: {
    label: 'Design Value',
    unit: 'string',
    arcGisKey: 'design_value',
  },
  cdd_final_url: {
    label: 'CDD Final URL',
    unit: 'link',
    arcGisKey: 'cdd_final_url',
  },
  statutory_attainment_date: {
    label: 'Statutory Attainment Date',
    unit: 'date',
    arcGisKey: 'statutory_attainment_date',
  },
  composite_id_with_state: {
    label: 'Composite ID with State',
    unit: 'string',
    arcGisKey: 'composite_id_with_state',
  },
  state_suffix: {
    label: 'State Suffix',
    unit: 'string',
    arcGisKey: 'state_suffix',
  },
  dv_units: {
    label: 'DV Units',
    unit: 'string',
    arcGisKey: 'dv_units',
  },
  classification_url: {
    label: 'Classification URL',
    unit: 'link',
    arcGisKey: 'classification_url',
  },
  dv_years: {
    label: 'DV Years',
    unit: 'string',
    arcGisKey: 'dv_years',
  },
  redesignation_final_citation: {
    label: 'Redesignation Final Citation',
    unit: 'string',
    arcGisKey: 'redesignation_final_citation',
  },
  cdd_effective_date: {
    label: 'CDD Effective Date',
    unit: 'date',
    arcGisKey: 'cdd_effective_date',
  },
  original_dv_years: {
    label: 'Original DV Years',
    unit: 'string',
    arcGisKey: 'original_dv_years',
  },
  daa_proposed_pub_date: {
    label: 'DAA Proposed Pub Date',
    unit: 'date',
    arcGisKey: 'daa_proposed_pub_date',
  },
  projected_redesignation_date: {
    label: 'Projected Redesignation Date',
    unit: 'date',
    arcGisKey: 'projected_redesignation_date',
  },
  cdd_final_pub_date: {
    label: 'CDD Final Pub Date',
    unit: 'date',
    arcGisKey: 'cdd_final_pub_date',
  },
  fips_state: {
    label: 'FIPS State',
    unit: 'string',
    arcGisKey: 'fips_state',
  },
  pollutant_name: {
    label: 'Pollutant Name',
    unit: 'string',
    arcGisKey: 'pollutant_name',
  },
  dv24: {
    label: 'DV24',
    unit: 'string',
    arcGisKey: 'dv24',
  },
  cdd_proposed_url: {
    label: 'CDD Proposed URL',
    unit: 'link',
    arcGisKey: 'cdd_proposed_url',
  },
  original_design_value: {
    label: 'Original Design Value',
    unit: 'string',
    arcGisKey: 'original_design_value',
  },
  redesignation_final_url: {
    label: 'Redesignation Final URL',
    unit: 'link',
    arcGisKey: 'redesignation_final_url',
  },
  // #endregion
  // #region --- Drayage --- //
  popup: {
    label: 'Rate Range',
    unit: 'string',
    arcGisKey: 'popup',
  },
  rateDataDa: {
    label: 'Rate Date',
    unit: 'date',
    arcGisKey: 'ratedatada',
  },
  // #region --- NPD Pipeline --- //
  esriField: {
    label: 'Description',
    unit: 'string',
    arcGisKey: 'esri_field',
  },
  submarket_: {
    label: 'Submarket',
    unit: 'string',
    arcGisKey: 'submarket_',
  },
  market: {
    label: 'Market',
    unit: 'string',
    arcGisKey: 'market',
  },
  lead: {
    label: 'Sourcing Lead/Analyst',
    unit: 'string',
    arcGisKey: 'lead',
  },
  mar_inv: {
    label: 'Market Inventory',
    unit: 'string',
    arcGisKey: 'mar_inv',
  },
  npd_inv: {
    label: 'NPD Inventory',
    unit: 'string',
    arcGisKey: 'npd_inv',
  },
  npd_vac: {
    label: 'NPD Vacancy',
    unit: 'string',
    arcGisKey: 'npd_vac',
  },
  npd_vacp: {
    label: 'NPD Vacancy %',
    unit: 'string',
    formatUnit: 'percentage',
    arcGisKey: 'npd_vacp',
  },
  npd_shar: {
    label: 'NPD Share',
    unit: 'string',
    arcGisKey: 'npd_shar',
  },
  mar_vac: {
    label: 'Market Vacancy',
    unit: 'string',
    arcGisKey: 'mar_vac',
  },
  mar_aban: {
    label: 'Prev. Year Net Abs.',
    unit: 'string',
    arcGisKey: 'mar_aban',
  },
  mar_ab5: {
    label: '5 Year Avg. Net Abs.',
    unit: 'string',
    arcGisKey: 'mar_ab5',
  },
  pipNon12: {
    label: 'Non-Bulk Pipeline: 1yr',
    unit: 'string',
    arcGisKey: 'pipnon12',
  },
  pipNon24: {
    label: 'Non-Bulk Pipeline: 2yr',
    unit: 'string',
    arcGisKey: 'pipnon24',
  },
  pipBul12: {
    label: 'Bulk Pipeline: 1yr',
    unit: 'string',
    arcGisKey: 'pipbul12',
  },
  pipBul24: {
    label: 'Bulk Pipeline: 2yr',
    unit: 'string',
    arcGisKey: 'pipbul24',
  },
  pipTot12: {
    label: 'Total Pipeline: 1yr',
    unit: 'string',
    arcGisKey: 'piptot12',
  },
  pipTot24: {
    label: 'Total Pipeline: 2yr',
    unit: 'string',
    arcGisKey: 'piptot24',
  },
  bal12: {
    label: 'Quarterly Absorption Growth to Balance (+12 Mo)',
    unit: 'string',
    arcGisKey: 'bal_12',
  },
  bal24: {
    label: 'Quarterly Absorption Growth to Balance (+24 Mo)',
    unit: 'string',
    arcGisKey: 'bal_24',
  },
  // #endregion
  // #region --- Insurance Hazards --- //
  hazard_typ: {
    label: 'Hazard Type',
    unit: 'string',
    arcGisKey: 'hazard_typ',
  },
  // #endregion
  // #region --- Markets and Submarkets --- //
  period: {
    label: 'Period',
    unit: 'string',
    arcGisKey: 'period',
  },
  acquisitionsRanking: {
    label: 'Acquisitions Rank',
    unit: 'number',
    arcGisKey: 'ranking',
  },
  rankingDescription: {
    label: 'Ranking Description',
    unit: 'string',
    arcGisKey: 'ranking_an',
  },
  totalInventory: {
    label: 'Total Inventory',
    unit: 'number',
    formatUnit: 'squareFeet',
    arcGisKey: 'total_inve',
  },
  delivered: {
    label: 'Delivered',
    unit: 'number',
    formatUnit: 'squareFeet',
    arcGisKey: 'delivered',
    bqHeader: 'Delivered Q',
  },
  vacancyQuarter: {
    label: 'Vacancy - Quarter',
    unit: 'number',
    formatUnit: 'percentage',
    arcGisKey: 'vacancy',
    bqHeader: 'Vacancy Q',
  },
  vacancyHistoric10Yr: {
    label: 'Vacancy - Historic 10 Yr Avg',
    unit: 'number',
    formatUnit: 'percentage',
    arcGisKey: 'vacancyhis',
  },
  netAbsorptionQuarter: {
    label: 'Net Abs. - Quarter',
    unit: 'number',
    formatUnit: 'squareFeet',
    arcGisKey: 'net_absorp',
    bqHeader: 'Net Absorption Q',
  },
  netAbsorptionRolling12Mth: {
    label: 'Net Abs. - Rolling 12 Mo',
    unit: 'number',
    formatUnit: 'squareFeet',
    arcGisKey: 'rolling_ne',
    bqHeader: 'Net Absorption Rolling 12 Mo',
  },
  netAbsorption10Yr: {
    label: 'Net Abs. - 10 Yr Ann Avg',
    unit: 'number',
    formatUnit: 'squareFeet',
    arcGisKey: 'average_an',
  },
  underConstructionQuarter: {
    label: 'Under Constr. - Quarter',
    unit: 'number',
    formatUnit: 'squareFeet',
    arcGisKey: 'under_cons',
    bqHeader: 'Under Construction Q',
  },
  underConstructionRolling12Mth: {
    label: 'Under Constr. Rolling 12 Mo',
    unit: 'number',
    formatUnit: 'squareFeet',
    arcGisKey: 'rolling_un',
    bqHeader: 'Under Construction Rolling 12 Mo',
  },
  rentQuarter: {
    label: 'Rent - Quarter',
    unit: 'number',
    formatUnit: 'pricePerSquareFoot',
    arcGisKey: 'asking_ren',
    bqHeader: 'Rent Q',
  },
  rentRolling12MthAvg: {
    label: 'Rent Rolling 12 Mo Avg',
    unit: 'number',
    formatUnit: 'pricePerSquareFoot',
    arcGisKey: 'rent_rolli',
  },
  rentRolling12MthPctChange: {
    label: 'Rent Rolling 12 Mo % Chg',
    unit: 'number',
    formatUnit: 'percentage',
    arcGisKey: 'rent_roll2',
    bqHeader: 'Rent Rolling 12 Mo Pct Chg',
  },
  leasingActivityQuarter: {
    label: 'Leasing Activity - Quarter',
    unit: 'number',
    formatUnit: 'squareFeet',
    arcGisKey: 'leasing_ac',
    bqHeader: 'Leasing Activity Q',
  },
  newBldgsAvgSize5Yr: {
    label: 'New Bldgs Avg Size (5 yrs)',
    unit: 'number',
    formatUnit: 'squareFeet',
    arcGisKey: 'newbldgsme',
  },
  newBldgsCount5Yr: {
    label: 'New Bldgs Count (5 yrs)',
    unit: 'number',
    arcGisKey: 'newbldgsco',
  },
  marketMomentumIndex: {
    label: 'Market Momentum Index',
    unit: 'number',
    arcGisKey: 'mmi',
    bqHeader: 'MMI',
  },
  supplyDemandImbalanceScore: {
    label: 'Supply-Demand Imbalance Score',
    unit: 'number',
    arcGisKey: 'sdis',
    bqHeader: 'SDIS',
  },
  recoveryPotentialMetric: {
    label: 'Recovery Potential Metric',
    unit: 'number',
    arcGisKey: 'rpm',
    bqHeader: 'RPM',
  },
  absorptionConstructionInteraction: {
    label: 'Absorption-Construction Interaction',
    unit: 'number',
    bqHeader: 'Abs_x_UC',
  },
  absorptionVacancyInteraction: {
    label: 'Absorption-Vacancy Interaction',
    unit: 'number',
    bqHeader: 'Abs_x_Vac',
  },
  constructionLeasingRisk: {
    label: 'Construction-Leasing Risk',
    unit: 'number',
    arcGisKey: 'lease_risk',
    bqHeader: 'UC_x_Lease_Risk',
  },
  // #endregion
  // #region --- Wetlands --- //
  acres: {
    label: 'Acres',
    unit: 'number',
    arcGisKey: 'acres',
    formatUnit: 'acreCount',
  },
  QAQCCode: {
    label: 'QAQC Code',
    unit: 'string',
    arcGisKey: 'qaqc_code',
  },
  // #endregion
  // #region --- Municipalities and Counties --- //
  layerType: {
    label: 'Type',
    unit: 'string',
    arcGisKey: 'layer_type',
  },
  // #endregion
  // #region --- Power Offtake Capacity --- //
  pocRecordId: {
    label: 'Record ID',
    unit: 'number',
    arcGisKey: 'recordid',
  },
  pocLandGateId: {
    label: 'LandGate ID',
    unit: 'string',
    arcGisKey: 'landgate_i',
  },
  pocOpenInfraId: {
    label: 'OpenInfra ID',
    unit: 'number',
    arcGisKey: 'openinfra_',
  },
  pocLGSubName: {
    label: 'LandGate Sub Name',
    unit: 'string',
    arcGisKey: 'lg_name',
  },
  pocOpenInfraSubName: {
    label: 'OpenInfra Sub Name',
    unit: 'string',
    arcGisKey: 'openinfra2',
  },
  pocLGType: {
    label: 'LandGate Type',
    unit: 'string',
    arcGisKey: 'lg_type',
  },
  pocOperator: {
    label: 'Operator',
    unit: 'string',
    arcGisKey: 'operator',
  },
  pocLGModelName: {
    label: 'LandGate Model Name',
    unit: 'string',
    arcGisKey: 'lg_model_n',
  },
  pocModelYear: {
    label: 'Model Year',
    unit: 'string',
    arcGisKey: 'model_year',
  },
  pocModelScenario: {
    label: 'Model Scenario',
    unit: 'string',
    arcGisKey: 'model_scen',
  },
  pocModelSource: {
    label: 'Model Source',
    unit: 'string',
    arcGisKey: 'model_sour',
  },
  pocModelPublishDate: {
    label: 'Model Publish Date',
    unit: 'string',
    arcGisKey: 'model_publ',
  },
  pocModelTransmissionRegion: {
    label: 'Model Transmission Region',
    unit: 'string',
    arcGisKey: 'model_tran',
  },
  pocModelSubRegion: {
    label: 'Model Sub Region',
    unit: 'string',
    arcGisKey: 'model_sub_',
  },
  pocModelSeason: {
    label: 'Model Season',
    unit: 'string',
    arcGisKey: 'model_seas',
  },
  pocBaseModelName: {
    label: 'Base Model Name',
    unit: 'string',
    arcGisKey: 'base_model',
  },
  pocBaseModelSeries: {
    label: 'Base Model Series',
    unit: 'string',
    arcGisKey: 'base_mode2',
  },
  pocMaxVoltage: {
    label: 'Max Voltage',
    unit: 'number',
    arcGisKey: 'max_voltag',
  },
  pocMinVoltage: {
    label: 'Min Voltage',
    unit: 'number',
    arcGisKey: 'min_voltag',
  },
  pocStatus: {
    label: 'Status',
    unit: 'string',
    arcGisKey: 'status',
  },
  pocNRISMinOfftakeCapacity: {
    label: 'NRIS - Min Offtake Capacity',
    unit: 'megaWatts',
    arcGisKey: 'ss_min_off',
  },
  pocNRISMaxOfftakeCapacity: {
    label: 'NRIS - Max Offtake Capacity',
    unit: 'megaWatts',
    arcGisKey: 'ss_max_off',
  },
  pocERISMinOfftakeCapacity: {
    label: 'ERIS - Min Offtake Capacity',
    unit: 'megaWatts',
    arcGisKey: 'ss_min_of2',
  },
  pocERISMaxOfftakeCapacity: {
    label: 'ERIS - Max Offtake Capacity',
    unit: 'megaWatts',
    arcGisKey: 'ss_max_of2',
  },
  pocNRISNoNegMinOfftakeCapacity: {
    label: 'NRIS - No Neg - Min Offtake Capacity',
    unit: 'megaWatts',
    arcGisKey: 'ss_min_of3',
  },
  pocNRISNoNegMaxOfftakeCapacity: {
    label: 'NRIS - No Neg - Max Offtake Capacity',
    unit: 'megaWatts',
    arcGisKey: 'ss_max_of3',
  },
  pocERISNoNegMinOfftakeCapacity: {
    label: 'ERIS - No Neg - Min Offtake Capacity',
    unit: 'megaWatts',
    arcGisKey: 'ss_min_of4',
  },
  pocERISNoNegMaxOfftakeCapacity: {
    label: 'ERIS - No Neg - Max Offtake Capacity',
    unit: 'megaWatts',
    arcGisKey: 'ss_max_of4',
  },
  pocERISOID: {
    label: 'ERIS OID',
    unit: 'string',
    arcGisKey: 'esri_oid',
  },
  // #endregion
  // #region --- Power Plants & Substations --- //
  ppssCity: {
    label: 'City',
    unit: 'string',
    arcGisKey: 'city',
  },
  ppssState: {
    label: 'State',
    unit: 'string',
    arcGisKey: 'state',
  },
  ppssZip: {
    label: 'Zip',
    unit: 'string',
    arcGisKey: 'zip',
  },
  ppssType: {
    label: 'Type',
    unit: 'string',
    arcGisKey: 'type',
  },
  ppssStatus: {
    label: 'Status',
    unit: 'string',
    arcGisKey: 'status',
  },
  ppssCounty: {
    label: 'County',
    unit: 'string',
    arcGisKey: 'county',
  },
  ppssCountyFips: {
    label: 'County FIPS',
    unit: 'string',
    arcGisKey: 'countyfips',
  },
  ppssCountry: {
    label: 'Country',
    unit: 'string',
    arcGisKey: 'country',
  },
  ppssLines: {
    label: 'Lines',
    unit: 'string',
    arcGisKey: 'lines',
  },
  ppssMaxVoltage: {
    label: 'Max Voltage',
    unit: 'string',
    arcGisKey: 'max_volt',
    formatUnit: 'kiloVolts',
  },
  ppssMinVoltage: {
    label: 'Min Voltage',
    unit: 'string',
    arcGisKey: 'min_volt',
    formatUnit: 'kiloVolts',
  },
  ppssLatitude: {
    label: 'Latitude',
    unit: 'string',
    arcGisKey: 'latitude',
  },
  ppssLongitude: {
    label: 'Longitude',
    unit: 'string',
    arcGisKey: 'longitude',
  },
  ppssElectricLoadZone: {
    label: 'Electric Load Zone',
    unit: 'string',
    arcGisKey: 'electric_load_zone',
  },
  ppssTransLines: {
    label: 'Transmission Lines',
    unit: 'string',
    arcGisKey: 'trans_lines',
  },
  ppssSubtransLines: {
    label: 'Subtransmission Lines',
    unit: 'string',
    arcGisKey: 'subtrans_lines',
  },
  ppssDistLines: {
    label: 'Distribution Lines',
    unit: 'string',
    arcGisKey: 'dist_lines',
  },
  ppssPlantCode: {
    label: 'Plant Code',
    unit: 'string',
    arcGisKey: 'plant_code',
  },
  ppssPlantName: {
    label: 'Plant Name',
    unit: 'string',
    arcGisKey: 'plant_name',
  },
  ppssOperatorName: {
    label: 'Operator Name',
    unit: 'string',
    arcGisKey: 'operator_name',
  },
  ppssUtilityId: {
    label: 'Utility ID',
    unit: 'string',
    arcGisKey: 'utility_id',
  },
  ppssUtilityName: {
    label: 'Utility Name',
    unit: 'string',
    arcGisKey: 'utility_name',
  },
  ppssNercRegion: {
    label: 'NERC Region',
    unit: 'string',
    arcGisKey: 'nerc_region',
  },
  ppssBalancingAuthorityCode: {
    label: 'Balancing Authority Code',
    unit: 'string',
    arcGisKey: 'balancing_authority_code',
  },
  ppssBalancingAuthorityName: {
    label: 'Balancing Authority Name',
    unit: 'string',
    arcGisKey: 'balancing_authority_name',
  },
  ppssSector: {
    label: 'Sector',
    unit: 'string',
    arcGisKey: 'sector',
  },
  ppssSectorName: {
    label: 'Sector Name',
    unit: 'string',
    arcGisKey: 'sector_name',
  },
  pssTransOrDistSystemOwner: {
    label: 'Trans or Dist Sys Owner',
    unit: 'string',
    arcGisKey: 'trans_or_dist_system_owner',
  },
  ppssTransOrDistSystemOwnerId: {
    label: 'Trans or Dist Sys Owner ID',
    unit: 'string',
    arcGisKey: 'trans_or_dist_system_owner_id',
  },
  ppssEnergyStorage: {
    label: 'Energy Storage',
    unit: 'string',
    arcGisKey: 'energy_storage',
  },
  ppssNameplateCapacity: {
    label: 'Nameplate Capacity',
    unit: 'number',
    arcGisKey: 'nameplate_capacity',
  },
  ppssCapacityOperating: {
    label: 'Capacity Operating',
    unit: 'number',
    arcGisKey: 'capacity_operating',
  },
  ppssCapacityBuilding: {
    label: 'Capacity Building',
    unit: 'number',
    arcGisKey: 'capacity_building',
  },
  ppssCapacityPlanned: {
    label: 'Capacity Planned',
    unit: 'number',
    arcGisKey: 'capacity_planned',
  },
  ppssCapacityMw: {
    label: 'Capacity MW',
    unit: 'number',
    formatUnit: 'megaWatts',
    arcGisKey: 'capacity_mw',
  },
  ppssPowerFactor: {
    label: 'Power Factor',
    unit: 'number',
    arcGisKey: 'power_factor',
  },
  ppssProdStart: {
    label: 'Prod Start',
    unit: 'date',
    arcGisKey: 'prod_start',
  },
  ppssFuelPrimary: {
    label: 'Fuel Primary',
    unit: 'string',
    arcGisKey: 'fuel_primary',
  },
  ppssFuelSecondary: {
    label: 'Fuel Secondary',
    unit: 'string',
    arcGisKey: 'fuel_secondary',
  },
  ppssPrimSource: {
    label: 'Primary Source',
    unit: 'string',
    arcGisKey: 'prim_source',
  },
  ppssTechnology: {
    label: 'Technology',
    unit: 'string',
    arcGisKey: 'technology',
  },
  ppssStreetAddress: {
    label: 'Street Address',
    unit: 'string',
    arcGisKey: 'street_address',
  },
  ppssOperator: {
    label: 'Operator',
    unit: 'string',
    arcGisKey: 'operator',
  },
  // #endregion
  // #region --- LandGate - Power Transmission Lines --- //
  lgTransLinesType: {
    label: 'Type',
    unit: 'string',
    arcGisKey: 'type',
  },
  lgTransLinesStatus: {
    label: 'Status',
    unit: 'string',
    arcGisKey: 'status',
  },
  lgTransLinesOwner: {
    label: 'Owner',
    unit: 'string',
    arcGisKey: 'owner',
  },
  lgTransLinesVoltage: {
    label: 'Voltage',
    unit: 'string',
    arcGisKey: 'voltage',
    formatUnit: 'kiloVolts',
  },
  lgTransLinesSub1: {
    label: 'Sub 1',
    unit: 'string',
    arcGisKey: 'sub_1',
  },
  lgTransLinesSub2: {
    label: 'Sub 2',
    unit: 'string',
    arcGisKey: 'sub_2',
  },
  lgTransLinesCounty: {
    label: 'County',
    unit: 'string',
    arcGisKey: 'county',
  },
  lgTransLinesState: {
    label: 'State',
    unit: 'string',
    arcGisKey: 'state',
  },
  lgTransLinesAmperage: {
    label: 'Conductor Amperage',
    unit: 'string',
    arcGisKey: 'amperage',
    formatUnit: 'amperes',
  },
  lgTransLinesMaxCapacity: {
    label: 'Max Capacity',
    unit: 'string',
    arcGisKey: 'max_capacity',
    formatUnit: 'megaWatts',
  },
  // #endregion
  // #region --- LandGate - Power Distribution Lines --- //
  lgDistributionCircuitName: {
    label: 'Circuit Name',
    unit: 'string',
    arcGisKey: 'circuit_name',
  },
  lgDistributionCounty: {
    label: 'County',
    unit: 'string',
    arcGisKey: 'county',
  },
  lgDistributionPeakAmps: {
    label: 'Peak Amps',
    unit: 'string',
    arcGisKey: 'peak_amps',
  },
  lgDistributionSummerRating: {
    label: 'Summer Rating',
    unit: 'string',
    arcGisKey: 'summer_rating',
  },
  lgDistributionFilterPhase: {
    label: 'Filter Phase',
    unit: 'string',
    arcGisKey: 'filter_phase',
  },
  lgDistributionLineName: {
    label: 'Line Name',
    unit: 'string',
    arcGisKey: 'line_name',
  },
  lgDistributionState: {
    label: 'State',
    unit: 'string',
    arcGisKey: 'state',
  },
  lgDistributionPhases: {
    label: 'Phases',
    unit: 'string',
    arcGisKey: 'phases',
  },
  lgDistributionOwner: {
    label: 'Owner',
    unit: 'string',
    arcGisKey: 'owner',
  },
  lgDistributionPhase: {
    label: 'Phase',
    unit: 'string',
    arcGisKey: 'phase',
  },
  lgDistributionCapacityLimit: {
    label: 'Capacity Limit',
    unit: 'string',
    arcGisKey: 'capacity_limit',
  },
  lgDistributionOverUnder: {
    label: 'Over/Under',
    unit: 'string',
    arcGisKey: 'over_under',
  },
  lgDistributionVoltage: {
    label: 'Voltage',
    unit: 'string',
    arcGisKey: 'voltage',
    formatUnit: 'kiloVolts',
  },
  lgDistributionHostingCapacity: {
    label: 'Hosting Capacity',
    unit: 'string',
    arcGisKey: 'hosting_capacity',
  },
  lgDistributionLoadZone: {
    label: 'Load Zone',
    unit: 'string',
    arcGisKey: 'load_zone',
  },
  lgDistributionLoadSubZone: {
    label: 'Load Subzone',
    unit: 'string',
    arcGisKey: 'load_subzone',
  },
  lgDistributionSub1: {
    label: 'Sub 1',
    unit: 'string',
    arcGisKey: 'sub_1',
  },
  lgDistributionLoadLink: {
    label: 'Load Link',
    unit: 'string',
    arcGisKey: 'load_link',
  },
  lgDistributionPenetrationLevel: {
    label: 'Penetration Level',
    unit: 'string',
    arcGisKey: 'penetration_level',
  },
  // #endregion
  // #region --- REIT Comps --- //
  reitName: {
    label: 'REIT Name',
    unit: 'string',
    arcGisKey: 'reit_name',
  },
  reitPropertyAddress: {
    label: 'Property Address',
    unit: 'string',
    arcGisKey: 'property_address',
  },
  reitCity: {
    label: 'City',
    unit: 'string',
    arcGisKey: 'city',
  },
  reitState: {
    label: 'State',
    unit: 'string',
    arcGisKey: 'state',
  },
  reitZip: {
    label: 'Zip',
    unit: 'string',
    arcGisKey: 'zip',
  },
  reitSize: {
    label: 'Size',
    unit: 'squareFeet',
    arcGisKey: 'size',
  },
  reitYearBuilt: {
    label: 'Year Built',
    unit: 'year',
    arcGisKey: 'year_built',
  },
  reitLastSalePrice: {
    label: 'Last Sale Price',
    unit: 'currency',
    arcGisKey: 'last_sale_price',
  },
  reitLastSaleDate: {
    label: 'Last Sale Date',
    unit: 'date',
    arcGisKey: 'last_sale_date',
  },
  reitClass: {
    label: 'Class',
    unit: 'string',
    arcGisKey: 'class',
  },
  reitOwnershipPercentage: {
    label: 'Ownership %',
    unit: 'percentage',
    arcGisKey: 'ownership__',
  },
  reitAssetManager: {
    label: 'Asset Manager',
    unit: 'string',
    arcGisKey: 'asset_manager',
  },
  reitAmPhone: {
    label: 'AM Phone',
    unit: 'string',
    arcGisKey: 'am_phone',
  },
  reitRegionalVp: {
    label: 'Regional VP',
    unit: 'string',
    arcGisKey: 'regional_vp',
  },
  reitRvpPhone: {
    label: 'RVP Phone',
    unit: 'string',
    arcGisKey: 'rvp_phone',
  },
  purchaseValue: {
    label: 'Purchase Value',
    unit: 'currency',
    arcGisKey: 'purchase_value',
  },
  propertyType: {
    label: 'Property Type',
    unit: 'string',
    arcGisKey: 'property_type',
  },
  region: {
    label: 'Region',
    unit: 'string',
    arcGisKey: 'region',
  },
  // #endregion
  // #region --- California HSR Alignment--- //
  cons_status: {
    label: 'Status',
    unit: 'string',
    arcGisKey: 'status',
  },
  cons_proj_name: {
    label: 'Name',
    unit: 'string',
    arcGisKey: 'name',
  },
  projectPageUrl: {
    label: 'Project Page URL',
    unit: 'link',
    arcGisKey: 'projectpageurl',
  },
  detour: {
    label: 'Detour',
    unit: 'string',
    arcGisKey: 'detour',
  },
  issueDate: {
    label: 'Issue Date',
    unit: 'date',
    arcGisKey: 'issuedate',
  },
  endDate: {
    label: 'End Date',
    unit: 'date',
    arcGisKey: 'enddate',
  },
  active: {
    label: 'Active',
    unit: 'string',
    arcGisKey: 'active',
  },
  stat_name: {
    label: 'Station Name',
    unit: 'string',
    arcGisKey: 'stat_name',
  },
  xStreets: {
    label: 'X Streets',
    unit: 'string',
    arcGisKey: 'x_streets',
  },
  phase: {
    label: 'Phase',
    unit: 'string',
    arcGisKey: 'phase',
  },
  section: {
    label: 'Section',
    unit: 'string',
    arcGisKey: 'section',
  },
  // #endregion
  // #region --- Multifamily Market Submarket --- //
  submarket: {
    label: 'Submarket',
    unit: 'string',
    arcGisKey: 'submarket',
  },
  slice: {
    label: 'Slice',
    unit: 'string',
    arcGisKey: 'slice',
  },
  geotype: {
    label: 'Geography Type',
    unit: 'string',
    arcGisKey: 'geotype',
  },
  totpopuln: {
    label: 'Total Population',
    unit: 'string',
    arcGisKey: 'totpopuln',
  },
  medhhincm: {
    label: 'Median Household Income',
    unit: 'currency',
    arcGisKey: 'medhhincm',
  },
  absorpunit: {
    label: 'Absorption Units',
    unit: 'string',
    arcGisKey: 'absorpunit',
  },
  assetvalue: {
    label: 'Asset Value',
    unit: 'currency',
    arcGisKey: 'assetvalue',
  },
  invunits: {
    label: 'Inventory Units',
    unit: 'string',
    arcGisKey: 'invunits',
  },
  underconst: {
    label: 'Under Construction Units',
    unit: 'string',
    arcGisKey: 'underconst',
  },
  startunits: {
    label: 'Construction Starts Units',
    unit: 'string',
    arcGisKey: 'startunits',
  },
  netdelivu: {
    label: 'Net Delivered Units',
    unit: 'string',
    arcGisKey: 'netdelivu',
  },
  stabvacnc: {
    label: 'Stabilized Vacancy',
    unit: 'percentage',
    arcGisKey: 'stabvacnc',
  },
  effrentpu: {
    label: 'Effective Rent per Unit',
    unit: 'currency',
    arcGisKey: 'effrentpu',
  },
  caprate: {
    label: 'Cap Rate',
    unit: 'percentage',
    arcGisKey: 'caprate',
  },
  year: {
    label: 'Year',
    unit: 'string',
    arcGisKey: 'year',
  },
  classainvu: {
    label: 'Class A Inventory Units',
    unit: 'string',
    arcGisKey: 'classainvu',
  },
  classbinvu: {
    label: 'Class B Inventory Units',
    unit: 'string',
    arcGisKey: 'classbinvu',
  },
  totsubinv: {
    label: 'Total Submarket Inventory',
    unit: 'string',
    arcGisKey: 'totsubinv',
  },
  pctclassa: {
    label: 'Pct Class A',
    unit: 'number',
    formatUnit: 'percentage',
    arcGisKey: 'pctclassa',
  },
  pctclassb: {
    label: 'Pct Class B',
    unit: 'number',
    formatUnit: 'percentage',
    arcGisKey: 'pctclassb',
  },
  rent4qchg: {
    label: 'Rolling 4Q Rent Nominal Change',
    unit: 'currency',
    arcGisKey: 'rent4qchg',
  },
  pop4qchg: {
    label: 'Rolling 4Q Population Nominal Change',
    unit: 'number',
    arcGisKey: 'pop4qchg',
  },
  uc8qave: {
    label: 'Under Construction Units 8Q Avg',
    unit: 'number',
    formatUnit: 'percentage',
    arcGisKey: 'uc8qave',
  },
  abs4qsum: {
    label: 'Absorption Units 4Q Sum',
    unit: 'string',
    arcGisKey: 'abs4qsum',
  },
  nd4qsum: {
    label: 'Net Delivered Units 4Q Sum',
    unit: 'string',
    arcGisKey: 'nd4qsum',
  },
  roll4qrchg: {
    label: 'Rolling 4Q Rent Change Pct',
    unit: 'number',
    formatUnit: 'percentage',
    arcGisKey: 'roll4qrchg',
  },
  roll4qpchg: {
    label: 'Rolling 4Q Population Change Pct',
    unit: 'number',
    formatUnit: 'percentage',
    arcGisKey: 'roll4qpchg',
  },
  abs4qpcti: {
    label: '4Q Absorption Pct of Inv',
    unit: 'number',
    formatUnit: 'percentage',
    arcGisKey: 'abs4qpcti',
  },
  rentpctinc: {
    label: 'Rent Pct of Income',
    unit: 'number',
    formatUnit: 'percentage',
    arcGisKey: 'rentpctinc',
  },
  rankdescrp: {
    label: 'Narrative',
    unit: 'string',
    arcGisKey: 'rankdescrp',
  },
  rankid: {
    label: 'Rank',
    unit: 'string',
    arcGisKey: 'rankid',
  },
  color: {
    label: 'Color',
    unit: 'string',
    arcGisKey: 'color',
  },
  transparen: {
    label: 'Transparency',
    unit: 'string',
    arcGisKey: 'transparen',
  },
  outline_co: {
    label: 'Outline Color',
    unit: 'string',
    arcGisKey: 'outline_co',
  },
  outline_wi: {
    label: 'Outline Width',
    unit: 'string',
    arcGisKey: 'outline_wi',
  },
  // #endregion
  // #region --- Lightbox Zoning Boundaries --- //
  lzbStateAbv: {
    label: 'State Abv',
    unit: 'string',
    arcGisKey: 'state_abv',
  },
  lzbType: {
    label: 'Zoning Type',
    unit: 'string',
    arcGisKey: 'zoning_type',
  },
  lzbCode: {
    label: 'Zoning Code',
    unit: 'string',
    arcGisKey: 'zoning_code',
  },
  lzbCategory: {
    label: 'Zoning Category',
    unit: 'string',
    arcGisKey: 'zoning_category',
  },
  lzbSubcategory: {
    label: 'Zoning Subcategory',
    unit: 'string',
    arcGisKey: 'zoning_subcategory',
  },
  lzbDescription: {
    label: 'Zoning Description',
    unit: 'string',
    arcGisKey: 'zoning_description',
  },
  // #endregion
  // #region --- Lightbox Parcels --- //
  lpParcelId: {
    label: 'Parcel ID',
    unit: 'string',
    arcGisKey: 'PARCEL_APN',
  },
  lpTaxId: {
    label: 'Tax ID',
    unit: 'string',
    arcGisKey: 'TAXAPN',
  },
  lpAddress: {
    label: 'Address',
    unit: 'string',
  },
  lpCounty: {
    label: 'County',
    unit: 'string',
    arcGisKey: 'COUNTY',
  },
  lpMunicipality: {
    label: 'Municipality',
    unit: 'string',
    arcGisKey: 'MUNICIPALITY',
  },
  lpOwner: {
    label: 'Owner',
    unit: 'string',
    arcGisKey: 'OWNER_NAME',
  },
  lpOwnerAddress: {
    label: 'Owner\'s Address',
    unit: 'string',
  },
  lpOwnerType: {
    label: 'Owner Type',
    unit: 'string',
    arcGisKey: 'OWNERSHIP_STATUS_DESC',
  },
  lpMarketLandValue: {
    label: 'Market Land Value',
    unit: 'number',
    formatUnit: 'currency',
    arcGisKey: 'VAL_MRKT_LAND',
  },
  lpMarketBuildingValue: {
    label: 'Market Building Value',
    unit: 'number',
    formatUnit: 'currency',
    arcGisKey: 'VAL_MRKT_IMPRV',
  },
  lpMarketTotalValue: {
    label: 'Market Total Value',
    unit: 'number',
    formatUnit: 'currency',
    arcGisKey: 'VAL_MRKET',
  },
  lpLandUseCode: {
    label: 'Land Use Code',
    unit: 'string',
    arcGisKey: 'USE_CODE_MUNI_DESC',
  },
  lpPropertyType: {
    label: 'Property Type',
    unit: 'string',
    arcGisKey: 'USE_CODE_STD_CTGR_DESC_LPS',
  },
  lpMailAddress: {
    label: 'Mail Address',
    unit: 'string',
  },
  lpAcreageCalculated: {
    label: 'LightBox Acreage Calculated',
    unit: 'number',
    formatUnit: 'acreage',
    arcGisKey: 'CAL_ACREAGE',
  },
  lpAcreageAssessed: {
    label: 'Acreage Assessed',
    unit: 'number',
    formatUnit: 'acreage',
    arcGisKey: 'ASSR_ACREAGE',
  },
  lpLotCount: {
    label: 'Lot Count',
    unit: 'number',
    arcGisKey: 'AGGR_LOT_COUNT',
  },
  lpZoning: {
    label: 'Zoning',
    unit: 'string',
    arcGisKey: 'ZONING',
  },
  // #endregion
  // #region --- Lightbox Parcel Zoning --- //
  lpzJurisdiction: {
    label: 'Jurisdiction',
    unit: 'string',
    arcGisKey: 'JURISDICTION_FULL_NAME',
  },
  lpzJurisdictionType: {
    label: 'Jurisdiction Type',
    unit: 'string',
    arcGisKey: 'JURISDICTION_TYPE',
  },
  lpzZoningType: {
    label: 'Zoning Type',
    unit: 'string',
    arcGisKey: 'ZONING_TYPE',
  },
  lpzZoningCode: {
    label: 'Zoning Code',
    unit: 'string',
    arcGisKey: 'ZONING_CODE',
  },
  lpzZoningCategory: {
    label: 'Zoning Category',
    unit: 'string',
    arcGisKey: 'ZONING_CATEGORY',
  },
  lpzZoningSubcategory: {
    label: 'Zoning Subcategory',
    unit: 'string',
    arcGisKey: 'ZONING_SUBCATEGORY',
  },
  lpzZoningDescription: {
    label: 'Zoning Description',
    unit: 'string',
    arcGisKey: 'ZONING_DESCRIPTION',
  },
  lpzZoningSummary: {
    label: 'Zoning Summary',
    unit: 'string',
    arcGisKey: 'ZONING_SUMMARY',
  },
  lpzPermittedUses: {
    label: 'Permitted Uses',
    unit: 'string',
    arcGisKey: 'PERMITTED_USES',
  },
  lpzMinLotArea: {
    label: 'Min Lot Area',
    unit: 'string',
    arcGisKey: 'MINIMUM_LOT_AREA_FULL_DESCRIPTI',
  },
  lpzMaxBuildingHeight: {
    label: 'Max Building Height',
    unit: 'number',
    formatUnit: 'feet',
    arcGisKey: 'MAXIMUM_BUILDING_HEIGHT_IN_FEET',
  },
  lpzDocumentLink: {
    label: 'Document Link',
    unit: 'link',
    arcGisKey: 'ORDINANCE_DOCUMENT_URL',
  },
  // #endregion
  // #region --- State Plane Zones --- //
  spzZone: {
    label: 'Zone',
    unit: 'string',
    arcGisKey: 'zone',
  },
  spzZoneName: {
    label: 'Zone Name',
    unit: 'string',
    arcGisKey: 'zonename',
  },
  spzFipsZone: {
    label: 'FIPS Zone',
    unit: 'string',
    arcGisKey: 'fipszone',
  },
  spzZoneType: {
    label: 'Zone Type',
    unit: 'string',
    arcGisKey: 'zonetype',
  },
  spzSqmiles: {
    label: 'Square Miles',
    unit: 'number',
    formatUnit: 'squareMiles',
    arcGisKey: 'sqmi',
  },
  spzCode: {
    label: 'C3D Code',
    unit: 'string',
    arcGisKey: 'c3d_code',
  },
  // #endregion
  // #region --- Opportunity Zones --- //
  ruralStat: {
    label: 'Rural Status',
    unit: 'string',
    arcGisKey: 'rural_stat',
  },
  tractName: {
    label: 'Tract Name',
    unit: 'string',
    arcGisKey: 'tract_name',
  },
  metroName: {
    label: 'Metro Name',
    unit: 'string',
    arcGisKey: 'metro_name',
  },
  // #endregion
  // #region --- School District Boundaries --- //
  sdName: {
    label: 'District Name',
    unit: 'string',
    arcGisKey: 'name',
  },
  sdGeoid: {
    label: 'District ID',
    unit: 'string',
    arcGisKey: 'geoid',
  },
  sdType: {
    label: 'District Type',
    unit: 'string',
    arcGisKey: 'sdtyp',
  },
  sdStateFips: {
    label: 'State',
    unit: 'string',
    arcGisKey: 'statefp',
  },
  sdLowestGrade: {
    label: 'Lowest Grade',
    unit: 'string',
    arcGisKey: 'lograde',
  },
  sdHighestGrade: {
    label: 'Highest Grade',
    unit: 'string',
    arcGisKey: 'higrade',
  },
  sdSchoolYear: {
    label: 'School Year',
    unit: 'string',
    arcGisKey: 'schoolyear',
  },
  sdLandArea: {
    label: 'Land Area',
    unit: 'number',
    formatUnit: 'squareMeters',
    arcGisKey: 'aland',
  },
  sdWaterArea: {
    label: 'Water Area',
    unit: 'number',
    formatUnit: 'squareMeters',
    arcGisKey: 'awater',
  },
  sdElsdLea: {
    label: 'Elementary LEA Code',
    unit: 'string',
    arcGisKey: 'elsdlea',
  },
  sdScsdLea: {
    label: 'Secondary LEA Code',
    unit: 'string',
    arcGisKey: 'scsdlea',
  },
  sdUnsdLea: {
    label: 'Unified LEA Code',
    unit: 'string',
    arcGisKey: 'unsdlea',
  },
  sdLsad: {
    label: 'Legal/Statistical Area Description',
    unit: 'string',
    arcGisKey: 'lsad',
  },
  sdFuncStat: {
    label: 'Functional Status',
    unit: 'string',
    arcGisKey: 'funcstat',
  },
  sdGeoYear: {
    label: 'TIGER Year',
    unit: 'string',
    arcGisKey: 'geo_year',
  },
  // #endregion
  // #region --- FedEx Locations --- //
  fedexAddress: {
    label: 'Address',
    unit: 'string',
    arcGisKey: 'address',
  },
  fedexCity: {
    label: 'City',
    unit: 'string',
    arcGisKey: 'city',
  },
  fedexState: {
    label: 'State',
    unit: 'string',
    arcGisKey: 'state',
  },
  fedexTenantName: {
    label: 'Tenant Name',
    unit: 'string',
    arcGisKey: 'tenant_nam',
  },
  fedexBuildingSize: {
    label: 'Building Size',
    unit: 'number',
    formatUnit: 'squareFeet',
    arcGisKey: 'sf_occupie',
  },
  fedexMovedIn: {
    label: 'Moved In',
    unit: 'string',
    arcGisKey: 'moved_in',
  },
  fedexCommencementDate: {
    label: 'Commencement Date',
    unit: 'string',
    arcGisKey: 'commenceme',
  },
  fedexOccupancy: {
    label: 'Occupancy',
    unit: 'string',
    arcGisKey: 'occupancy_',
  },
  fedexMarket: {
    label: 'Market',
    unit: 'string',
    arcGisKey: 'market',
  },
  fedexSubmarket: {
    label: 'Submarket',
    unit: 'string',
    arcGisKey: 'submarket',
  },
  fedexLandlord: {
    label: 'Landlord',
    unit: 'string',
    arcGisKey: 'landlord',
  },
  // #endregion
  // #region --- Orennia Transmission Network Diagram --- //
  otnIso: {
    label: 'ISO',
    unit: 'string',
    arcGisKey: 'iso',
  },
  otnFromBusId: {
    label: 'From Bus ID',
    unit: 'string',
    arcGisKey: 'from_bus_id',
  },
  otnFromBusName: {
    label: 'From Bus Name',
    unit: 'string',
    arcGisKey: 'from_bus_name',
  },
  otnToBusId: {
    label: 'To Bus ID',
    unit: 'string',
    arcGisKey: 'to_bus_id',
  },
  otnToBusName: {
    label: 'To Bus Name',
    unit: 'string',
    arcGisKey: 'to_bus_name',
  },
  otnVoltage: {
    label: 'Voltage',
    unit: 'number',
    formatUnit: 'kiloVolts',
    arcGisKey: 'voltage',
  },
  otnCircuitId: {
    label: 'Circuit ID',
    unit: 'string',
    arcGisKey: 'circuit_id',
  },
  otnScenario: {
    label: 'Scenario',
    unit: 'string',
    arcGisKey: 'scenario',
  },
  otnDescription: {
    label: 'Description',
    unit: 'string',
    arcGisKey: 'description',
  },
  otnCapacity0Upgrades: {
    label: '0 Upgrade Capacity',
    unit: 'number',
    formatUnit: 'megaWattsWhole',
    arcGisKey: 'capacity_0_upgrades',
  },
  otnCapacity1Upgrades: {
    label: '1 Upgrade Capacity',
    unit: 'number',
    formatUnit: 'megaWattsWhole',
    arcGisKey: 'capacity_1_upgrades',
  },
  otnCapacity2Upgrades: {
    label: '2 Upgrade Capacity',
    unit: 'number',
    formatUnit: 'megaWattsWhole',
    arcGisKey: 'capacity_2_upgrades',
  },
  otnCapacity3Upgrades: {
    label: '3 Upgrade Capacity',
    unit: 'number',
    formatUnit: 'megaWattsWhole',
    arcGisKey: 'capacity_3_upgrades',
  },
  otnCapacity4Upgrades: {
    label: '4 Upgrade Capacity',
    unit: 'number',
    formatUnit: 'megaWattsWhole',
    arcGisKey: 'capacity_4_upgrades',
  },
  otnBranchRating1: {
    label: 'Rating Normal',
    unit: 'number',
    formatUnit: 'megaVoltAmperes',
    arcGisKey: 'branch_rating_1',
  },
  otnBranchRating2: {
    label: 'Rating Contingency',
    unit: 'number',
    formatUnit: 'megaVoltAmperes',
    arcGisKey: 'branch_rating_2',
  },
  otnBranchId: {
    label: 'Branch ID',
    unit: 'string',
    arcGisKey: 'branch_id',
  },
  otnPrimaryLimitingConstraint: {
    label: 'Limiting Constraint',
    unit: 'string',
    arcGisKey: 'primary_limiting_constraint',
  },
  otnDescriptivePowerflowCase: {
    label: 'Descriptive Powerflow Case',
    unit: 'string',
    bqHeader: 'DESCRIPTIVE_POWERFLOW_CASE',
    arcGisKey: 'descriptive_powerflow_case',
  },
  // #endregion
  // #region --- Orennia Substations --- //
  osSubstationName: {
    label: 'Substation Name',
    unit: 'string',
    arcGisKey: 'substation_name',
  },
  osCounty: {
    label: 'County',
    unit: 'string',
    arcGisKey: 'county',
  },
  osState: {
    label: 'State',
    unit: 'string',
    arcGisKey: 'state',
  },
  osStatus: {
    label: 'Status',
    unit: 'string',
    arcGisKey: 'status',
  },
  osType: {
    label: 'Type',
    unit: 'string',
    arcGisKey: 'type',
  },
  osOwner: {
    label: 'Owner',
    unit: 'string',
    arcGisKey: 'owner',
  },
  osMaxVoltage: {
    label: 'Max Voltage',
    unit: 'number',
    formatUnit: 'kiloVolts',
    arcGisKey: 'max_voltage',
  },
  osMinVoltage: {
    label: 'Min Voltage',
    unit: 'number',
    formatUnit: 'kiloVolts',
    arcGisKey: 'min_voltage',
  },
  osSubstationId: {
    label: 'Substation ID',
    unit: 'string',
    arcGisKey: 'substation_id',
  },
  osNameSource: {
    label: 'Source',
    unit: 'string',
    arcGisKey: 'name_source',
  },
  // #endregion
  // #region --- Exit Cap Rate --- //
  low: {
    label: 'Low',
    unit: 'number',
    formatUnit: 'percentage',
    arcGisKey: 'low',
  },
  mid: {
    label: 'Mid',
    unit: 'number',
    formatUnit: 'percentage',
    arcGisKey: 'mid',
  },
  high: {
    label: 'High',
    unit: 'number',
    formatUnit: 'percentage',
    arcGisKey: 'high',
  },
  // #endregion
  // #region --- Orennia Buses --- //
  obBusName: {
    label: 'Bus Name',
    unit: 'string',
    arcGisKey: 'bus_name',
  },
  obBusId: {
    label: 'Bus ID',
    unit: 'string',
    arcGisKey: 'bus_id',
  },
  obBusVoltage: {
    label: 'Bus Voltage',
    unit: 'number',
    formatUnit: 'kiloVolts',
    arcGisKey: 'bus_voltage',
  },
  obOwner: {
    label: 'Owner',
    unit: 'string',
    arcGisKey: 'owner',
  },
  obState: {
    label: 'State',
    unit: 'string',
    arcGisKey: 'state',
  },
  obCounty: {
    label: 'County',
    unit: 'string',
    arcGisKey: 'county',
  },
  obReportedPowerflowCase: {
    label: 'Reported Powerflow Case',
    unit: 'string',
    arcGisKey: 'reported_powerflow_case',
  },
  obCap0: {
    label: 'Interconn. Capacity (Tier 0)',
    unit: 'number',
    formatUnit: 'megaWatts',
    arcGisKey: 'cap_0',
  },
  obCap1: {
    label: 'Interconn. Capacity (Tier 1)',
    unit: 'number',
    formatUnit: 'megaWatts',
    arcGisKey: 'cap_1',
  },
  obCap2: {
    label: 'Interconn. Capacity (Tier 2)',
    unit: 'number',
    formatUnit: 'megaWatts',
    arcGisKey: 'cap_2',
  },
  obCap3: {
    label: 'Interconn. Capacity (Tier 3)',
    unit: 'number',
    formatUnit: 'megaWatts',
    arcGisKey: 'cap_3',
  },
  obCap4: {
    label: 'Interconn. Capacity (Tier 4)',
    unit: 'number',
    formatUnit: 'megaWatts',
    arcGisKey: 'cap_4',
  },
  obBaseFlow0: {
    label: 'Base Flow (Tier 0)',
    unit: 'number',
    formatUnit: 'megaWatts',
    arcGisKey: 'base_flow_0',
  },
  obBaseFlow1: {
    label: 'Base Flow (Tier 1)',
    unit: 'number',
    formatUnit: 'megaWatts',
    arcGisKey: 'base_flow_1',
  },
  obBaseFlow2: {
    label: 'Base Flow (Tier 2)',
    unit: 'number',
    formatUnit: 'megaWatts',
    arcGisKey: 'base_flow_2',
  },
  obBaseFlow3: {
    label: 'Base Flow (Tier 3)',
    unit: 'number',
    formatUnit: 'megaWatts',
    arcGisKey: 'base_flow_3',
  },
  obBaseFlow4: {
    label: 'Base Flow (Tier 4)',
    unit: 'number',
    formatUnit: 'megaWatts',
    arcGisKey: 'base_flow_4',
  },
  obCont0: {
    label: 'Contingency Flow (Tier 0)',
    unit: 'number',
    formatUnit: 'megaWatts',
    arcGisKey: 'cont_0',
  },
  obCont1: {
    label: 'Contingency Flow (Tier 1)',
    unit: 'number',
    formatUnit: 'megaWatts',
    arcGisKey: 'cont_1',
  },
  obCont2: {
    label: 'Contingency Flow (Tier 2)',
    unit: 'number',
    formatUnit: 'megaWatts',
    arcGisKey: 'cont_2',
  },
  obCont3: {
    label: 'Contingency Flow (Tier 3)',
    unit: 'number',
    formatUnit: 'megaWatts',
    arcGisKey: 'cont_3',
  },
  obCont4: {
    label: 'Contingency Flow (Tier 4)',
    unit: 'number',
    formatUnit: 'megaWatts',
    arcGisKey: 'cont_4',
  },
  obRanges: {
    label: 'Ranges',
    unit: 'string',
    arcGisKey: 'ranges',
  },
  obUpgradeCapacity0: {
    label: '0 Upgrade Capacity',
    unit: 'number',
    formatUnit: 'megaWattsWhole',
    arcGisKey: 'upgrade_capacity0',
  },
  obUpgradeCapacity1: {
    label: '1 Upgrade Capacity',
    unit: 'number',
    formatUnit: 'megaWattsWhole',
    arcGisKey: 'upgrade_capacity1',
  },
  obUpgradeCapacity2: {
    label: '2 Upgrade Capacity',
    unit: 'number',
    formatUnit: 'megaWattsWhole',
    arcGisKey: 'upgrade_capacity2',
  },
  obUpgradeCapacity3: {
    label: '3 Upgrade Capacity',
    unit: 'number',
    formatUnit: 'megaWattsWhole',
    arcGisKey: 'upgrade_capacity3',
  },
  obUpgradeCapacity4: {
    label: '4 Upgrade Capacity',
    unit: 'number',
    formatUnit: 'megaWattsWhole',
    arcGisKey: 'upgrade_capacity4',
  },
  // #endregion
  // #region --- Orennia Buses Interconnection Cost (Snowflake grid) --- //
  oicScenario: {
    label: 'Scenario',
    unit: 'string',
    bqHeader: 'SCENARIO',
  },
  oicSensitivityCase: {
    label: 'Sensitivity Case',
    unit: 'string',
    bqHeader: 'SENSITIVITY_CASE',
  },
  oicBusId: {
    label: 'Bus ID',
    unit: 'string',
    bqHeader: 'BUS_ID',
  },
  oicInterconnectionType: {
    label: 'Interconnection Type',
    unit: 'string',
    bqHeader: 'INTERCONNECTION_TYPE',
  },
  oicProxyInterconnectionCapacity: {
    label: 'Proxy Interconnection Capacity',
    unit: 'number',
    formatUnit: 'megaWatts',
    bqHeader: 'PROXY_INTERCONNECTION_CAPACITY',
  },
  oicTotalUpgradeCost: {
    label: 'Interconnection Cost Estimate',
    unit: 'number',
    formatUnit: 'currencyMillions',
    bqHeader: 'TOTAL_UPGRADE_COST',
  },
  oicNumberOfUpgrades: {
    label: 'Number of Upgrades',
    unit: 'number',
    bqHeader: 'NUMBER_OF_UPGRADES',
  },
  oicUpgradeIndex: {
    label: 'Upgrade Index',
    unit: 'number',
    bqHeader: 'UPGRADE_INDEX',
  },
  oicDfaxAc: {
    label: 'DFAX AC',
    unit: 'number',
    bqHeader: 'DFAX_AC',
  },
  oicViolatedConstraint: {
    label: 'Violated Constraint',
    unit: 'string',
    bqHeader: 'VIOLATED_CONSTRAINT',
  },
  oicViolatedConstraintBranchId: {
    label: 'Violated Constraint Branch ID',
    unit: 'string',
    bqHeader: 'VIOLATED_CONSTRAINT_BRANCH_ID',
  },
  oicContingency: {
    label: 'Contingency',
    unit: 'string',
    bqHeader: 'CONTINGENCY',
  },
  oicConstraintType: {
    label: 'Constraint Type',
    unit: 'string',
    bqHeader: 'CONSTRAINT_TYPE',
  },
  oicConstraintRating: {
    label: 'Constraint Rating',
    unit: 'number',
    formatUnit: 'megaVoltAmperes',
    bqHeader: 'CONSTRAINT_RATING',
  },
  oicConstraintLength: {
    label: 'Constraint Length',
    unit: 'number',
    formatUnit: 'miles',
    bqHeader: 'CONSTRAINT_LENGTH',
  },
  oicLengthSource: {
    label: 'Length Source',
    unit: 'string',
    bqHeader: 'LENGTH_SOURCE',
  },
  oicConstraintKvs: {
    label: 'Constraint kV',
    unit: 'number',
    formatUnit: 'kiloVolts',
    bqHeader: 'CONSTRAINT_KVS',
  },
  oicAcLoadingRatioPercentage: {
    label: 'AC Loading Ratio',
    unit: 'number',
    formatUnit: 'percentage',
    bqHeader: 'AC_LOADING_RATIO_PERCENTAGE',
  },
  oicExistingOverloadFlag: {
    label: 'Existing Overload Flag',
    unit: 'string',
    bqHeader: 'EXISTING_OVERLOAD_FLAG',
  },
  oicRiskLevel: {
    label: 'Risk Level',
    unit: 'string',
    bqHeader: 'RISK_LEVEL',
  },
  oicMaxLeadTime: {
    label: 'Max Lead Time',
    unit: 'number',
    formatUnit: 'months',
    bqHeader: 'MAX_LEAD_TIME',
  },
  oicMinLeadTime: {
    label: 'Min Lead Time',
    unit: 'number',
    formatUnit: 'months',
    bqHeader: 'MIN_LEAD_TIME',
  },
  oicDfaxCutoffRatio: {
    label: 'DFAX Cutoff Ratio',
    unit: 'number',
    bqHeader: 'DFAX_CUTOFF_RATIO',
  },
  oicBeforeInterconnectionFlow: {
    label: 'Before Interconnection Flow',
    unit: 'number',
    formatUnit: 'megaWatts',
    bqHeader: 'BEFORE_INTERCONNECTION_FLOW',
  },
  oicFlowContribution: {
    label: 'Flow Contribution',
    unit: 'number',
    formatUnit: 'megaWatts',
    bqHeader: 'FLOW_CONTRIBUTION',
  },
  oicPostInterconnectionFlow: {
    label: 'Post Interconnection Flow',
    unit: 'number',
    formatUnit: 'megaWatts',
    bqHeader: 'POST_INTERCONNECTION_FLOW',
  },
  oicUpgradeType: {
    label: 'Upgrade Type',
    unit: 'string',
    bqHeader: 'UPGRADE_TYPE',
  },
  oicUpgradeRating: {
    label: 'Upgrade Rating',
    unit: 'number',
    formatUnit: 'megaVoltAmperes',
    bqHeader: 'UPGRADE_RATING',
  },
  oicUpgradeUnitCost: {
    label: 'Upgrade Unit Cost',
    unit: 'number',
    formatUnit: 'currencyMillions',
    bqHeader: 'UPGRADE_UNIT_COST',
  },
  oicAchievableRating: {
    label: 'Achievable Rating',
    unit: 'number',
    formatUnit: 'megaVoltAmperes',
    bqHeader: 'ACHIEVABLE_RATING',
  },
  oicIndividualUpgradeCost: {
    label: 'Individual Upgrade Cost',
    unit: 'number',
    formatUnit: 'currencyMillions',
    bqHeader: 'INDIVIDUAL_UPGRADE_COST',
  },
  oicPowerflowCase: {
    label: 'Powerflow Case',
    unit: 'string',
    bqHeader: 'POWERFLOW_CASE',
  },
  oicDescriptivePowerflowCase: {
    label: 'Descriptive Powerflow Case',
    unit: 'string',
    bqHeader: 'DESCRIPTIVE_POWERFLOW_CASE',
  },
  oicStudyYear: {
    label: 'Study Year',
    unit: 'number',
    formatUnit: 'year',
    bqHeader: 'STUDY_YEAR',
  },
  oicGeneratorModelingAssumptions: {
    label: 'Generator Modeling Assumptions',
    unit: 'string',
    bqHeader: 'GENERATOR_MODELING_ASSUMPTIONS',
  },
  oicTransmissionModelingAssumptions: {
    label: 'Transmission Modeling Assumptions',
    unit: 'string',
    bqHeader: 'TRANSMISSION_MODELING_ASSUMPTIONS',
  },
  oicCaseLastUpdated: {
    label: 'Case Last Updated',
    unit: 'date',
    bqHeader: 'CASE_LAST_UPDATED',
  },
  // #endregion
  // #region --- Orennia Power Plants (Snowflake grid) --- //
  oppType: {
    label: 'Power Project Type',
    unit: 'string',
    arcGisKey: 'type',
  },
  oppCapacity: {
    label: 'Power Project Capacity',
    unit: 'number',
    formatUnit: 'megaWatts',
    arcGisKey: 'capacity_mw',
  },
  oppGeneratorName: {
    label: 'Generator Name',
    unit: 'string',
    arcGisKey: 'generator_name',
  },
  oppOwner: {
    label: 'Owner',
    unit: 'string',
    arcGisKey: 'owner',
  },
  oppStatus: {
    label: 'Status',
    unit: 'string',
    arcGisKey: 'status',
  },
  oppAvgHeatRate: {
    label: 'Average Heat Rate',
    unit: 'number',
    formatUnit: 'btuPerKWh',
    arcGisKey: 'heat_rate_mean_btu_per_kwh',
  },
  oppCapex: {
    label: 'Capex',
    unit: 'number',
    formatUnit: 'currency',
    arcGisKey: 'capex',
  },
  oppCapexPerWatt: {
    label: 'Capex Per Watt',
    unit: 'number',
    formatUnit: 'currency',
    arcGisKey: 'capex_usd_per_w',
  },
  // #endregion
  // #region --- Data Center Comps --- //
  dccPlantID: {
    label: 'Plant ID',
    unit: 'string',
    arcGisKey: 'plant_id',
  },
  dccParentName: {
    label: 'Parent Name',
    unit: 'string',
    arcGisKey: 'parent_name',
  },
  dccOwnerName: {
    label: 'Owner Name',
    unit: 'string',
    arcGisKey: 'owner_name',
  },
  dccCapacity: {
    label: 'Capacity',
    unit: 'string',
    formatUnit: 'megaWatts',
    arcGisKey: 'capacity',
  },
  dccSquareFootage: {
    label: 'Square Footage',
    unit: 'string',
    formatUnit: 'squareFeet',
    arcGisKey: 'square_footage',
  },
  dccStatus: {
    label: 'Status',
    unit: 'string',
    arcGisKey: 'status',
  },
  dccISORegion: {
    label: 'ISO RTO Region',
    unit: 'string',
    arcGisKey: 'iso_rto_region',
  },
  dccProjectCount: {
    label: 'Project Count',
    unit: 'string',
    arcGisKey: 'project_count',
  },
  dccConstructionDuration: {
    label: 'Construction Duration (months) with counts',
    unit: 'string',
    arcGisKey: 'construction_duration__months__with_counts',
  },
  dccTIVCounts: {
    label: 'TIV USD with counts',
    unit: 'string',
    arcGisKey: 'tiv_usd_with_counts',
  },
  dccType: {
    label: 'Project Type with counts',
    unit: 'string',
    arcGisKey: 'project_type_with_counts',
  },
  dccStage: {
    label: 'Project Stage with counts',
    unit: 'string',
    arcGisKey: 'project_stage_with_counts',
  },
  dccKickoff: {
    label: 'Kickoff Date with counts',
    unit: 'string',
    arcGisKey: 'kickoff_date_with_counts',
  },
  dccCompletionDate: {
    label: 'Completion Date with counts',
    unit: 'string',
    arcGisKey: 'completion_date_with_counts',
  },
  dccHyperscaler: {
    label: 'Hyperscaler Flag',
    unit: 'string',
    arcGisKey: 'hyperscaler_flag',
  },
  // #endregion
};

export default fieldTypes;


